/* Projectprijs aanvragen page component with form for custom plant orders
   Updated: Renamed from 'Offerte aanvragen' to 'Projectprijs aanvragen' with creative wording
   Updated: Unified layout to match BlogsPage - bg-white, max-w-7xl, jv-primary headings */

import { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { trackGoal } from '@/lib/datafast';
import { CircleCheck as CheckCircle, Loader as Loader2, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ContactBar from '@/components/ContactBar';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import PageBreadcrumb from '@/components/PageBreadcrumb';

export default function OfferteAanvragenPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    plant_name: '',
    plant_height: '',
    quantity: '1',
    delivery_method: 'binnen_nl',
    postal_code: '',
    preferred_contact_method: 'mail',
    optional_message: '',
    aanplantservice: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.name.trim()) {
      toast.error('Vul je naam in');
      return;
    }

    if (!formData.email.trim() || !formData.email.includes('@')) {
      toast.error('Vul een geldig e-mailadres in');
      return;
    }

    if (!formData.plant_name.trim()) {
      toast.error('Vul de plantnaam in');
      return;
    }

    if (!formData.plant_height.trim()) {
      toast.error('Vul de hoogte van de plant in');
      return;
    }

    if (!formData.quantity || parseInt(formData.quantity) < 1) {
      toast.error('Vul een geldig aantal in (minimaal 1)');
      return;
    }

    if (!formData.postal_code.trim()) {
      toast.error('Vul je postcode in');
      return;
    }

    setIsSubmitting(true);

    try {
      // Insert into Supabase
      const { data: quoteData, error } = await supabase.from('quote_requests').insert({
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        phone: formData.phone.trim() || null,
        plant_name: formData.plant_name.trim(),
        plant_height: formData.plant_height.trim(),
        quantity: parseInt(formData.quantity),
        delivery_method: formData.delivery_method,
        postal_code: formData.postal_code.trim(),
        preferred_contact_method: formData.preferred_contact_method,
        optional_message: formData.optional_message.trim() || null,
        aanplantservice: formData.aanplantservice,
      }).select('id').maybeSingle();

      if (error) {
        console.error('Error submitting quote request:', error);
        toast.error('Er is een fout opgetreden. Probeer het opnieuw.');
        return;
      }

      setIsSuccess(true);
      trackGoal('quote_submitted');
      toast.success('Aanvraag voor projectprijs verzonden!');

      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        plant_name: '',
        plant_height: '',
        quantity: '1',
        delivery_method: 'binnen_nl',
        postal_code: '',
        preferred_contact_method: 'mail',
        optional_message: '',
        aanplantservice: false,
      });
    } catch (error) {
      console.error('Error submitting quote request:', error);
      toast.error('Er is een fout opgetreden. Probeer het opnieuw.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Projectprijs aanvragen bij Buitn Planten</title>
        <meta name="description" content="Vraag een vrijblijvende projectprijs aan voor je haagplanten of tuinplanten. Persoonlijk advies en een scherpe prijs op maat." />
        <link rel="canonical" href="https://buitnplanten.nl/projectprijs-aanvragen" />
      </Helmet>

      <ContactBar />
      <Header />

      <main data-fast-scroll="offerte_page_viewed" className="pt-8 pb-16 bg-white">
        <div className="max-w-7xl mx-auto px-8 sm:px-12 lg:px-16">
          <PageBreadcrumb items={[{ label: 'Home', href: '/' }, { label: 'Projectprijs aanvragen' }]} />
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl font-bold text-jv-primary mb-6">
              Projectprijs aanvragen
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Op zoek naar A-kwaliteit haagplanten of tuinplanten voor een scherpe prijs? Bij onze kwekerij in Dedemsvaart ben je aan het juiste adres. Of je nu particulier bent of als hovenier/projectontwikkelaar zakelijk inkoopt, wij maken jou graag een vrijblijvende projectprijs op maat waarmee je gegarandeerd tevreden bent. Vul onderstaand formulier in en je ontvangt snel (vaak binnen één werkdag) een prijs die aansluit op jouw groene plannen.
            </p>
          </div>

          {!isSuccess ? (
            <div className="max-w-4xl mx-auto bg-jv-secondary shadow-md p-10 md:p-12 rounded-tl-[4rem] rounded-br-[4rem]">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Name */}
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-jv-dark font-semibold">
                      Naam <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="name"
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      placeholder="Je volledige naam"
                      required
                    />
                  </div>

                  {/* Email */}
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-jv-dark font-semibold">
                      E-mailadres <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder="je.email@voorbeeld.nl"
                      required
                    />
                  </div>

                  {/* Phone */}
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-jv-dark font-semibold">
                      Telefoonnummer <span className="text-gray-500 text-sm font-normal">(optioneel)</span>
                    </Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      placeholder="06 12345678"
                    />
                  </div>

                  {/* Plant Name */}
                  <div className="space-y-2">
                    <Label htmlFor="plant_name" className="text-jv-dark font-semibold">
                      Plantnaam <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="plant_name"
                      type="text"
                      value={formData.plant_name}
                      onChange={(e) => handleInputChange('plant_name', e.target.value)}
                      placeholder="Bijv. Taxus Baccata"
                      required
                    />
                  </div>

                  {/* Plant Height */}
                  <div className="space-y-2">
                    <Label htmlFor="plant_height" className="text-jv-dark font-semibold">
                      Hoogte van de plant <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="plant_height"
                      type="text"
                      value={formData.plant_height}
                      onChange={(e) => handleInputChange('plant_height', e.target.value)}
                      placeholder="Bijv. 100-120 cm"
                      required
                    />
                  </div>

                  {/* Quantity */}
                  <div className="space-y-2">
                    <Label htmlFor="quantity" className="text-jv-dark font-semibold">
                      Aantal <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="quantity"
                      type="number"
                      min="1"
                      value={formData.quantity}
                      onChange={(e) => handleInputChange('quantity', e.target.value)}
                      placeholder="1"
                      required
                    />
                  </div>

                  {/* Delivery Method */}
                  <div className="space-y-3">
                    <Label className="text-jv-dark font-semibold">
                      Bezorgmethode <span className="text-red-500">*</span>
                    </Label>
                    <RadioGroup
                      value={formData.delivery_method}
                      onValueChange={(value) => handleInputChange('delivery_method', value)}
                      className="space-y-2"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="binnen_nl" id="binnen_nl" />
                        <Label htmlFor="binnen_nl" className="font-normal cursor-pointer">
                          Bezorgen binnen Nederland
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="buiten_nl" id="buiten_nl" />
                        <Label htmlFor="buiten_nl" className="font-normal cursor-pointer">
                          Bezorgen buiten Nederland
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="afhalen" id="afhalen" />
                        <Label htmlFor="afhalen" className="font-normal cursor-pointer">
                          Afhalen bij kwekerij
                        </Label>
                      </div>
                    </RadioGroup>
                  </div>

                  {/* Postal Code */}
                  <div className="space-y-2">
                    <Label htmlFor="postal_code" className="text-jv-dark font-semibold">
                      Postcode <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="postal_code"
                      type="text"
                      value={formData.postal_code}
                      onChange={(e) => handleInputChange('postal_code', e.target.value)}
                      placeholder="1234 AB"
                      required
                    />
                  </div>

                  {/* Preferred Contact Method */}
                  <div className="space-y-3">
                    <Label className="text-jv-dark font-semibold">
                      Projectprijs ontvangen via <span className="text-red-500">*</span>
                    </Label>
                    <RadioGroup
                      value={formData.preferred_contact_method}
                      onValueChange={(value) => handleInputChange('preferred_contact_method', value)}
                      className="space-y-2"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="mail" id="mail" />
                        <Label htmlFor="mail" className="font-normal cursor-pointer">
                          E-mail
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="whatsapp" id="whatsapp" />
                        <Label htmlFor="whatsapp" className="font-normal cursor-pointer">
                          WhatsApp
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="gebeld_worden" id="gebeld_worden" />
                        <Label htmlFor="gebeld_worden" className="font-normal cursor-pointer">
                          Gebeld worden
                        </Label>
                      </div>
                    </RadioGroup>
                  </div>

                  {/* Aanplantservice Checkbox */}
                  <div className="flex items-start space-x-3">
                    <Checkbox
                      id="aanplantservice"
                      checked={formData.aanplantservice}
                      onCheckedChange={(checked) =>
                        setFormData(prev => ({ ...prev, aanplantservice: checked === true }))
                      }
                    />
                    <label htmlFor="aanplantservice" className="text-sm leading-tight cursor-pointer text-jv-dark">
                      Ik wil graag gebruik maken van de{' '}
                      <a
                        href="/klantenservice/aanplantservice"
                        className="text-jv-primary font-medium underline hover:no-underline"
                      >
                        Aanplantservice
                      </a>
                    </label>
                  </div>

                  {/* Optional Message */}
                  <div className="space-y-2">
                    <Label htmlFor="optional_message" className="text-jv-dark font-semibold">
                      Optioneel bericht
                    </Label>
                    <Textarea
                      id="optional_message"
                      value={formData.optional_message}
                      onChange={(e) => handleInputChange('optional_message', e.target.value)}
                      placeholder="Heb je nog specifieke wensen of vragen? Laat het ons weten..."
                      rows={4}
                      className="resize-none"
                    />
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-jv-primary hover:bg-jv-primary/90 text-white py-6 text-lg font-bold"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Verzenden...
                      </>
                    ) : (
                      'Vraag mijn projectprijs aan!'
                    )}
                  </Button>
                </form>
              </motion.div>
            </div>
          ) : (
            <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-md p-8 sm:p-12 text-center">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4 }}
              >
                <CheckCircle className="h-20 w-20 text-green-500 mx-auto mb-6" />
                <h2 className="text-3xl font-bold text-jv-primary mb-4">Bedankt voor je aanvraag!</h2>
                <p className="text-lg text-gray-700 mb-8">
                  Wij hebben je aanvraag voor een projectprijs ontvangen en zullen zo snel mogelijk contact met je opnemen via je gekozen contactmethode.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button
                    asChild
                    className="bg-jv-primary hover:bg-jv-primary/90 text-white"
                  >
                    <Link to="/">
                      <ArrowLeft className="mr-2 h-4 w-4" />
                      Terug naar home
                    </Link>
                  </Button>
                  <Button
                    asChild
                    variant="outline"
                    className="border-2 border-jv-primary hover:bg-jv-primary/10"
                  >
                    <Link to="/haagplanten">Bekijk haagplanten</Link>
                  </Button>
                </div>
              </motion.div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}
